import React, {useState, useEffect, useRef} from 'react'
import {makeStyles, Theme, IconButton} from '@material-ui/core'
import RecordVoiceOverIcon from '@material-ui/icons/RecordVoiceOver'
import VoiceOverOffIcon from '@material-ui/icons/VoiceOverOff'

// Import or define the new state type directly
type ContinuousCycleStage = 'idle' | 'listening'

interface StyleProps {
  isActive: boolean
  cycleStage: ContinuousCycleStage
  disabled: boolean
}

const useStyles = makeStyles<Theme, StyleProps>((theme) => ({
  customButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 40,
    height: 40,
    borderRadius: '50%',
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    padding: 8,
    color: ({isActive, disabled}) =>
      disabled
        ? theme.palette.action.disabled
        : isActive
        ? theme.palette.secondary.main // Keep active color consistent
        : theme.palette.text.secondary,
    opacity: ({disabled}) => (disabled ? 0.7 : 1),
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
    '&:focus': {
      outline: 'none',
    },
    transition: 'background-color 0.3s',
    position: 'relative',
    overflow: 'hidden',
    WebkitTapHighlightColor: 'transparent',
    '&::after': {
      content: '""',
      display: 'none',
    },
  },
  badge: {
    position: 'relative',
    display: 'inline-flex',
  },
  dot: {
    position: 'absolute',
    top: 5,
    right: 3,
    width: 8,
    height: 8,
    borderRadius: '50%',
    // Update background color logic based on new states
    backgroundColor: ({cycleStage, disabled, isActive}) => {
      if (disabled || !isActive) return 'transparent' // No dot if disabled or inactive

      switch (cycleStage) {
        case 'listening': // Represents recording state
          return theme.palette.error.main // Red dot for listening/hearing
        case 'idle':
        default:
          return 'transparent' // No dot when idle
      }
    },
  },
  tooltipDisabled: {
    cursor: 'not-allowed',
  },
  wrapper: {
    display: 'inline-block',
    lineHeight: 0,
    position: 'relative',
  },
  staticIcon: {
    fontSize: 24,
    color: 'inherit',
  },
  tooltip: {
    position: 'absolute',
    bottom: -35,
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: 'rgba(97, 97, 97, 0.9)',
    color: '#fff',
    padding: '5px 10px',
    borderRadius: 4,
    fontSize: 12,
    whiteSpace: 'nowrap',
    zIndex: 1000,
    opacity: 0,
    visibility: 'hidden',
    transition: 'opacity 0.2s',
    boxShadow: '0px 2px 4px rgba(0,0,0,0.2)',
  },
  showTooltip: {
    opacity: 1,
    visibility: 'visible',
  },
}))

interface ContinuousVoiceToggleProps {
  /** Whether continuous voice mode is active */
  isActive: boolean
  /** Current stage in the continuous voice cycle */
  cycleStage: ContinuousCycleStage // Use the new type
  /** Callback when toggle is clicked */
  onToggle: (event: React.MouseEvent<HTMLButtonElement>) => void
  /** Optional additional CSS class */
  className?: string
  /** Whether the toggle is disabled */
  disabled?: boolean
}

/**
 * A toggle button for enabling/disabling continuous voice interaction
 * Shows different colors/animations based on the current stage of the cycle
 */
const ContinuousVoiceToggle: React.FC<ContinuousVoiceToggleProps> = ({
  isActive,
  cycleStage, // Use the new prop name/type
  onToggle,
  className,
  disabled = false,
}) => {
  const [showTooltip, setShowTooltip] = useState(false)
  const tooltipTimeout = useRef<number | null>(null)
  const classes = useStyles({isActive, cycleStage, disabled})

  // Clean up any timeouts when component unmounts
  useEffect(
    () => () => {
      if (tooltipTimeout.current !== null) {
        window.clearTimeout(tooltipTimeout.current)
      }
    },
    []
  )

  // Update tooltip text based on new states
  const getTooltipText = () => {
    if (disabled) return 'Click to enable microphone for voice mode'
    if (!isActive) return 'Enable Continuous Voice Mode'

    switch (cycleStage) {
      case 'listening':
        return 'Listening... (click to disable)'
      default:
        // idle
        return 'Continuous Voice Mode Enabled (click to disable)'
    }
  }

  // Handle mouse events for tooltip
  const handleMouseEnter = () => {
    if (tooltipTimeout.current) {
      window.clearTimeout(tooltipTimeout.current)
    }
    tooltipTimeout.current = window.setTimeout(() => setShowTooltip(true), 300)
  }

  const handleMouseLeave = () => {
    if (tooltipTimeout.current) {
      window.clearTimeout(tooltipTimeout.current)
    }
    tooltipTimeout.current = window.setTimeout(() => setShowTooltip(false), 100)
  }

  // Update dot visibility logic based on new states
  const showDot = isActive && !['idle'].includes(cycleStage) && !disabled

  return (
    <div
      className={classes.wrapper}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className={classes.badge}>
        <IconButton
          onClick={onToggle}
          className={`${classes.customButton} ${className || ''}`}
          aria-label={
            isActive
              ? 'Disable continuous voice mode'
              : 'Enable continuous voice mode'
          }
          disabled={disabled}
          size="medium"
        >
          {isActive ? <RecordVoiceOverIcon /> : <VoiceOverOffIcon />}
        </IconButton>
        {showDot && <span className={classes.dot} />}
      </div>

      {/* Custom tooltip implementation */}
      <div
        className={`${classes.tooltip} ${
          showTooltip ? classes.showTooltip : ''
        }`}
      >
        {getTooltipText()}
      </div>
    </div>
  )
}

// Use React.memo but without any extra dependencies
export default React.memo(ContinuousVoiceToggle)
